export type BudgetRuleKind = 'EVENT_DAILY' | 'SCHEDULE_WEEKLY' | 'PERFORMANCE_DAILY';

export type BudgetRuleFormState = {
  kind: BudgetRuleKind;
  ruleName: string;
  increasePct: string;
  startDate: string;
  endDate: string;
  eventId: string;
  daysOfWeek: string[];
  threshold: string;
  comparisonOperator: string;
  metricName: string;
  ruleState: 'ACTIVE' | 'PAUSED';
};

export function toAmazonDate(value: string): string {
  return value.replace(/-/g, '');
}

export function fromAmazonDate(value: string | undefined | null): string {
  if (value == null || typeof value !== 'string' || value.length !== 8) return '';
  return `${value.slice(0, 4)}-${value.slice(4, 6)}-${value.slice(6, 8)}`;
}

export function detectBudgetRuleKind(row: Record<string, unknown>): BudgetRuleKind {
  const details =
    row.ruleDetails && typeof row.ruleDetails === 'object'
      ? (row.ruleDetails as Record<string, unknown>)
      : {};
  const ruleType = String(details.ruleType ?? '');
  const duration =
    details.duration && typeof details.duration === 'object'
      ? (details.duration as Record<string, unknown>)
      : {};
  const eventDur = duration.eventTypeRuleDuration as Record<string, unknown> | null | undefined;
  if (ruleType === 'SCHEDULE' && eventDur?.eventId) return 'EVENT_DAILY';
  const recurrence =
    details.recurrence && typeof details.recurrence === 'object'
      ? (details.recurrence as Record<string, unknown>)
      : {};
  if (ruleType === 'SCHEDULE' && String(recurrence.type ?? '') === 'WEEKLY') return 'SCHEDULE_WEEKLY';
  return 'PERFORMANCE_DAILY';
}

export function budgetRuleRowToFormState(row: Record<string, unknown>): BudgetRuleFormState {
  const details =
    row.ruleDetails && typeof row.ruleDetails === 'object'
      ? (row.ruleDetails as Record<string, unknown>)
      : {};
  const kind = detectBudgetRuleKind(row);
  const increase = details.budgetIncreaseBy as Record<string, unknown> | undefined;
  const increaseVal = increase?.value;
  const duration =
    details.duration && typeof details.duration === 'object'
      ? (details.duration as Record<string, unknown>)
      : {};
  const dateRange = duration.dateRangeTypeRuleDuration as Record<string, unknown> | null | undefined;
  const eventDur = duration.eventTypeRuleDuration as Record<string, unknown> | null | undefined;
  const recurrence =
    details.recurrence && typeof details.recurrence === 'object'
      ? (details.recurrence as Record<string, unknown>)
      : {};
  const perf = details.performanceMeasureCondition as Record<string, unknown> | null | undefined;
  const days = recurrence.daysOfWeek;
  const ruleStateRaw = typeof row.ruleState === 'string' ? row.ruleState.toUpperCase() : 'ACTIVE';

  return {
    kind,
    ruleName: typeof details.name === 'string' ? details.name : '',
    increasePct:
      typeof increaseVal === 'number'
        ? String(increaseVal)
        : typeof increaseVal === 'string'
          ? increaseVal
          : '25',
    startDate: fromAmazonDate(
      dateRange?.startDate != null ? String(dateRange.startDate) : undefined
    ),
    endDate: fromAmazonDate(dateRange?.endDate != null ? String(dateRange.endDate) : undefined),
    eventId: eventDur?.eventId != null ? String(eventDur.eventId) : '',
    daysOfWeek: Array.isArray(days)
      ? days.filter((d): d is string => typeof d === 'string')
      : ['MONDAY'],
    threshold:
      perf?.threshold != null
        ? String(perf.threshold)
        : '20',
    comparisonOperator:
      typeof perf?.comparisonOperator === 'string'
        ? perf.comparisonOperator
        : 'LESS_THAN_OR_EQUAL_TO',
    metricName: typeof perf?.metricName === 'string' ? perf.metricName : 'ACOS',
    ruleState: ruleStateRaw === 'PAUSED' ? 'PAUSED' : 'ACTIVE',
  };
}

export type BuildCreatePayloadInput = {
  kind: BudgetRuleKind;
  ruleName: string;
  defaultName?: string;
  increasePct: string;
  startDate: string;
  endDate: string;
  eventId: string;
  daysOfWeek: string[];
  threshold: string;
  comparisonOperator: string;
};

export function buildCreateBudgetRulePayload(
  input: BuildCreatePayloadInput
): { payload: Record<string, unknown> | null; error: string | null } {
  const pct = parseFloat(input.increasePct);
  if (Number.isNaN(pct) || pct <= 0) {
    return { payload: null, error: 'Budget increase percent must be greater than 0.' };
  }

  const ruleBase: Record<string, unknown> = {
    name: input.ruleName || input.defaultName || 'Budget rule',
    budgetIncreaseBy: { type: 'PERCENT', value: pct },
  };

  if (input.kind === 'EVENT_DAILY') {
    if (!input.eventId.trim()) {
      return { payload: null, error: 'Event ID is required for event based daily rule.' };
    }
    return {
      payload: {
        ...ruleBase,
        ruleType: 'SCHEDULE',
        duration: { eventTypeRuleDuration: { eventId: input.eventId.trim() } },
        recurrence: { type: 'DAILY' },
      },
      error: null,
    };
  }

  if (input.kind === 'SCHEDULE_WEEKLY') {
    if (!input.startDate || !input.endDate) {
      return {
        payload: null,
        error: 'Start date and end date are required for scheduled weekly rule.',
      };
    }
    if (input.daysOfWeek.length === 0) {
      return { payload: null, error: 'Select at least one day for weekly recurrence.' };
    }
    return {
      payload: {
        ...ruleBase,
        ruleType: 'SCHEDULE',
        duration: {
          dateRangeTypeRuleDuration: {
            startDate: toAmazonDate(input.startDate),
            endDate: toAmazonDate(input.endDate),
          },
        },
        recurrence: { type: 'WEEKLY', daysOfWeek: input.daysOfWeek },
      },
      error: null,
    };
  }

  if (!input.startDate || !input.endDate) {
    return {
      payload: null,
      error: 'Start date and end date are required for performance daily rule.',
    };
  }
  const thresholdNum = parseFloat(input.threshold);
  if (Number.isNaN(thresholdNum)) {
    return { payload: null, error: 'Threshold must be a valid number.' };
  }
  return {
    payload: {
      ...ruleBase,
      ruleType: 'PERFORMANCE',
      duration: {
        dateRangeTypeRuleDuration: {
          startDate: toAmazonDate(input.startDate),
          endDate: toAmazonDate(input.endDate),
        },
      },
      recurrence: { type: 'DAILY' },
      performanceMeasureCondition: {
        metricName: 'ACOS',
        threshold: thresholdNum,
        comparisonOperator: input.comparisonOperator,
      },
    },
    error: null,
  };
}

export function buildStateOnlyUpdatePayload(
  row: Record<string, unknown>,
  ruleState: 'ACTIVE' | 'PAUSED'
): { payload: Record<string, unknown> | null; error: string | null } {
  const ruleId = typeof row.ruleId === 'string' ? row.ruleId : '';
  if (!ruleId) return { payload: null, error: 'Missing rule id.' };
  return {
    payload: { ruleId, ruleState },
    error: null,
  };
}

export function buildUpdateBudgetRulePayload(
  row: Record<string, unknown>,
  form: BudgetRuleFormState
): { payload: Record<string, unknown> | null; error: string | null } {
  const ruleId = typeof row.ruleId === 'string' ? row.ruleId : '';
  if (!ruleId) return { payload: null, error: 'Missing rule id.' };

  const existingDetails =
    row.ruleDetails && typeof row.ruleDetails === 'object'
      ? ({ ...(row.ruleDetails as Record<string, unknown>) } as Record<string, unknown>)
      : {};

  const pct = parseFloat(form.increasePct);
  if (Number.isNaN(pct) || pct <= 0) {
    return { payload: null, error: 'Budget increase percent must be greater than 0.' };
  }

  const ruleDetails: Record<string, unknown> = {
    ...existingDetails,
    name: form.ruleName || (typeof existingDetails.name === 'string' ? existingDetails.name : 'Budget rule'),
    budgetIncreaseBy: {
      type: 'PERCENT',
      value: pct,
    },
  };

  const kind = detectBudgetRuleKind(row);

  if (kind === 'EVENT_DAILY') {
    const duration =
      existingDetails.duration && typeof existingDetails.duration === 'object'
        ? ({ ...(existingDetails.duration as Record<string, unknown>) } as Record<string, unknown>)
        : {};
    const eventDur =
      duration.eventTypeRuleDuration && typeof duration.eventTypeRuleDuration === 'object'
        ? ({ ...(duration.eventTypeRuleDuration as Record<string, unknown>) } as Record<string, unknown>)
        : {};
    ruleDetails.duration = {
      ...duration,
      eventTypeRuleDuration: {
        ...eventDur,
        eventId: form.eventId.trim() || eventDur.eventId,
      },
    };
    ruleDetails.recurrence = { type: 'DAILY' };
    ruleDetails.ruleType = 'SCHEDULE';
    delete ruleDetails.performanceMeasureCondition;
  } else if (kind === 'SCHEDULE_WEEKLY') {
    if (!form.startDate || !form.endDate) {
      return {
        payload: null,
        error: 'Start date and end date are required for scheduled weekly rule.',
      };
    }
    if (form.daysOfWeek.length === 0) {
      return { payload: null, error: 'Select at least one day for weekly recurrence.' };
    }
    ruleDetails.ruleType = 'SCHEDULE';
    ruleDetails.duration = {
      dateRangeTypeRuleDuration: {
        startDate: toAmazonDate(form.startDate),
        endDate: toAmazonDate(form.endDate),
      },
      eventTypeRuleDuration: null,
    };
    ruleDetails.recurrence = { type: 'WEEKLY', daysOfWeek: form.daysOfWeek };
    delete ruleDetails.performanceMeasureCondition;
  } else {
    if (!form.startDate || !form.endDate) {
      return {
        payload: null,
        error: 'Start date and end date are required for performance daily rule.',
      };
    }
    const thresholdNum = parseFloat(form.threshold);
    if (Number.isNaN(thresholdNum)) {
      return { payload: null, error: 'Threshold must be a valid number.' };
    }
    ruleDetails.ruleType = 'PERFORMANCE';
    ruleDetails.duration = {
      dateRangeTypeRuleDuration: {
        startDate: toAmazonDate(form.startDate),
        endDate: toAmazonDate(form.endDate),
      },
      eventTypeRuleDuration: null,
    };
    ruleDetails.recurrence = { type: 'DAILY' };
    ruleDetails.performanceMeasureCondition = {
      metricName: form.metricName,
      threshold: thresholdNum,
      comparisonOperator: form.comparisonOperator,
    };
  }

  const ruleStatus =
    typeof row.ruleStatus === 'string' ? row.ruleStatus : 'ACTIVE';

  return {
    payload: {
      ruleId,
      ruleState: form.ruleState,
      ruleStatus,
      ruleStatusDetails: row.ruleStatusDetails ?? null,
      lastUpdatedDate: null,
      ruleDetails,
    },
    error: null,
  };
}

export function budgetRuleKindLabel(kind: BudgetRuleKind): string {
  const map: Record<BudgetRuleKind, string> = {
    EVENT_DAILY: 'Event-based (daily)',
    SCHEDULE_WEEKLY: 'Scheduled (weekly)',
    PERFORMANCE_DAILY: 'Performance (daily)',
  };
  return map[kind];
}
